/* eslint-disable @typescript-eslint/no-empty-interface,
@typescript-eslint/no-misused-promises, class-methods-use-this */
import { PaginationState, RowSelectionState } from '@tanstack/react-table';
import moment from 'moment';
import React, { Component } from 'react';
import { confirmWrapper } from 'src/components/Dialog';
import withRouter from '../../hooks/withRouter';
import API from '../../network';
import { TalentPoolFiltersSubmitHandler } from './components/TalentPoolFilters';

import {
  BookmarkProfileRequestBody,
  TalentPoolCandidateRecord,
  TalentPoolProfilesRequestBody,
  TalentPoolProps,
  TalentPoolResponse,
  TalentPoolState,
  TalentPoolViewRendererProps,
} from './types';

import ViewRenderer from './views';

class TalentPool extends Component<TalentPoolProps, TalentPoolState> {
  constructor(props: TalentPoolProps) {
    super(props);

    this.state = {
      profilesFetching: false,
      bookmarksFetching: false,
      rowSelection: {},
      records: [],
      bookmarkedRecords: [],
      filters: {
        pagination: {
          pageIndex: 0,
          pageSize: 20,
          totalRecords: 0,
        },
      },
    };
  }

  componentDidMount(): void {
    const { filters } = this.state;
    const { router } = this.props;
    const { location } = router;
    const { recruiterId, jobOrderId } = location.state || {};
    if (recruiterId || jobOrderId) {
      this.setState({
        filters: {
          ...filters,
          talentpool: {
            ...filters.talentpool,
            jobOrderId,
            recruiterId,
          },
        },
      }, () => {
        this.fetchProfiles();
        this.fetchBookmarks();
        window.history.replaceState({}, '');
      });
    } else {
      this.fetchProfiles();
      this.fetchBookmarks();
    }
  }

  fetchBookmarks = () => {
    this.setState({
      bookmarksFetching: true,
    }, async () => {
      try {
        const reponse = await API.getInstance().getBookmarkedCandidateProfiles();
        const { tableData } = reponse;
        this.setState({
          bookmarkedRecords: tableData,
          bookmarksFetching: true,
        });
      } catch (e) {
        await confirmWrapper('Something went wrong', {
          hideSecondaryBtn: true,
          variant: 'danger',
        });
      } finally {
        this.setState({
          bookmarksFetching: false,
        });
      }
    });
  };

  fetchProfiles = () => {
    this.setState(
      {
        profilesFetching: true,
      },
      async () => {
        const {
          filters,
        } = this.state;
        const { pagination, talentpool } = filters;

        const requestBody: TalentPoolProfilesRequestBody = {
          page: pagination.pageIndex,
          size: pagination.pageSize.toString(),
          ...talentpool,
        };

        try {
          const talentpoolProfilesResponse: TalentPoolResponse = await API.getInstance()
            .getTalentPoolProfiles(requestBody);
          this.setState({
            records: talentpoolProfilesResponse.tableData,
            filters: {
              ...filters,
              pagination: {
                ...filters.pagination,
                totalRecords: talentpoolProfilesResponse.totalElements,
              },
            },
            rowSelection: {},
          });
        } catch (e) {
          await confirmWrapper('Something went wrong', {
            hideSecondaryBtn: true,
            variant: 'danger',
          });
        } finally {
          this.setState({
            profilesFetching: false,
          });
        }
      },
    );
  };

  bookmarkToggleApi = async (requestBody: BookmarkProfileRequestBody) => {
    try {
      await API.getInstance()
        .bookmarkCandidateProfile(requestBody);
      this.fetchProfiles();
      this.fetchBookmarks();
    } catch (e) {
      await confirmWrapper('Something went wrong', {
        hideSecondaryBtn: true,
        variant: 'danger',
      });
    } finally {
      this.setState({
        profilesFetching: false,
      });
    }
  };

  onRowSelectionChange = (data: RowSelectionState) => {
    this.setState({ rowSelection: data });
  };

  onChangePagination = (data: PaginationState) => {
    const { filters } = this.state;
    this.setState(
      {
        filters: {
          ...filters,
          pagination: { ...filters.pagination, ...data },
        },
      },
      () => {
        this.fetchProfiles();
      },
    );
  };

  onRowClick = (row: TalentPoolCandidateRecord) => {
    const url = `${window.location.origin}/candidate-profile/${row.hashKey}`;
    console.log(url);
    window.open(url, '_blank');
  };

  onApplyFilter = (data: TalentPoolFiltersSubmitHandler) => {
    const { filters } = this.state;
    const { values } = data;

    const {
      dateRange, searchQuery, title, email, consultancy, clients,
      jobs, source, location, recruiter, status, scores,
    } = values;

    const startDate = dateRange?.startDate ? moment(dateRange?.startDate).format('MM/DD/YYYY') : undefined;
    const endDate = dateRange?.endDate ? moment(dateRange?.endDate).format('MM/DD/YYYY') : undefined;

    const quickSearch = searchQuery;
    const candidateTitle = title;
    const emailAddress = email;
    const consultancyName = consultancy;
    const clientName = clients?.toString();
    const jobOrderId = jobs;
    const recruiterId = recruiter;
    const candidateStatus = status;
    const startTechRating = scores?.pannaScoreLive?.min || undefined;
    const endTechRating = scores?.pannaScoreLive?.max || undefined;

    const startCommRating = scores?.pannaScoreLive?.min || undefined;
    const endCommRating = scores?.pannaScoreLive?.max || undefined;

    const startPannaScore = scores?.pannaScore?.min || undefined;
    const endPannaScore = scores?.pannaScore?.max || undefined;

    this.setState(
      {
        records: [],
        filters: {
          ...filters,
          talentpool: {
            source,
            location,
            startDate,
            endDate,
            quickSearch,
            candidateTitle,
            emailAddress,
            consultancyName,
            clientName,
            jobOrderId,
            candidateStatus,
            recruiterId,
            startTechRating,
            endTechRating,
            startCommRating,
            endCommRating,
            startPannaScore,
            endPannaScore,
          },
        },
      },
      () => {
        this.fetchProfiles();
      },
    );
  };

  onFiltersReset = () => {
    const { filters, profilesFetching } = this.state;
    if (profilesFetching) {
      return;
    }
    this.setState({
      filters: {
        ...filters,
        talentpool: {},
      },
      records: [],
    }, () => {
      this.fetchProfiles();
    });
  };

  bookmarkProfile = async (record: TalentPoolCandidateRecord) => {
    await this.bookmarkToggleApi({
      candidateId: record.candidateId,
      status: !record.isBookMarked,
    });
  };

  onAfterJobApply = () => {
    this.setState({
      rowSelection: {},
    }, () => {
      this.fetchProfiles();
    });
  };

  getProps = (): TalentPoolViewRendererProps => {
    const {
      rowSelection,
      filters,
      records,
      profilesFetching,
      bookmarkedRecords,
      bookmarksFetching,
    } = this.state;

    return {
      profilesFetching,
      records,
      rowSelection,
      filters,
      bookmarkedRecords,
      bookmarksFetching,
      bookmarkProfile: this.bookmarkProfile,
      onFiltersReset: this.onFiltersReset,
      onRowClick: this.onRowClick,
      onApplyFilter: this.onApplyFilter,
      onChangePagination: this.onChangePagination,
      onRowSelectionChange: this.onRowSelectionChange,
      onAfterJobApply: this.onAfterJobApply,
    };
  };

  render() {
    const viewProps: TalentPoolViewRendererProps = this.getProps();

    return (
      <ViewRenderer {...viewProps} />
    );
  }
}

export default withRouter(TalentPool);
